import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lunchMenu, alaCarteMenu } from '../data/menuData';
import { restaurantInfo } from '../data/restaurantInfo';
import { useTranslation } from '../hooks/useTranslation';
import PrintMenuButton from './PrintMenuButton';
import { Clock, Utensils, Leaf, Flame, Sparkles } from 'lucide-react';
import { getLunchWeek } from '../utils/lunchWeek';
import ChefPickBadge from './ChefPickBadge';
import ShareLunch from './ShareLunch';
import { useDailyMenus } from '../hooks/useDailyMenus';
import { locText, origName, lunchUi, locBase } from '../utils/lunchLocale';

const EnhancedMenuPage = () => {
  const [activeMenu, setActiveMenu] = useState('lunch');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterVegan, setFilterVegan] = useState(false);
  const [filterGF, setFilterGF] = useState(false);
  const [showAllergenPopup, setShowAllergenPopup] = useState(false);

  // Compute today in Europe/Helsinki regardless of visitor's device timezone
  const helsinkiNow = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Helsinki',
        weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date());
      const weekdayShort = parts.find(p => p.type === 'weekday')?.value || 'Mon';
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return { dow: weekdayMap[weekdayShort] ?? 1, hour, minute };
    } catch {
      const d = new Date();
      return { dow: d.getDay(), hour: d.getHours(), minute: d.getMinutes() };
    }
  })();

  const [selectedDay, setSelectedDay] = useState(() => {
    const dayMap = { 0: 'weekend', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'weekend' };
    return dayMap[helsinkiNow.dow] || 'monday';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [saucesExpanded, setSaucesExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, currentLanguage: lang } = useTranslation();
  const dailyMenus = useDailyMenus();
  const ui = lunchUi(lang);

  useEffect(() => {
    const category = searchParams.get('category');
    const view = searchParams.get('view');
    if (category === 'alacarte' || view === 'alacarte') {
      setActiveMenu('alacarte');
      setSelectedCategory('appetizers');
    } else {
      setActiveMenu('lunch');
    }
  }, [searchParams]);

  // Get current day and calculate dates for each day of the week
  const now = new Date();
  const currentDayOfWeek = helsinkiNow.dow;
  const currentDay = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][currentDayOfWeek];
  const isSaturday = currentDayOfWeek === 6;
  const isSunday = currentDayOfWeek === 0;

  // Live buffet status (buffet 11:00–15:00 Mon–Fri, 12:00–16:00 Sat, closed Sun)
  const liveStatus = (() => {
    const { dow, hour, minute } = helsinkiNow;
    const nowMin = hour * 60 + minute;
    if (dow === 0) return { kind: 'closed', color: '⚪', label: 'Buffet suljettu sunnuntaina', fi: 'Buffet suljettu' };
    const openMin = dow === 6 ? 12 * 60 : 11 * 60;
    const closeMin = dow === 6 ? 16 * 60 : 15 * 60;
    const openStr = dow === 6 ? '12:00' : '11:00';
    const closeStr = dow === 6 ? '16:00' : '15:00';
    if (nowMin < openMin) return { kind: 'soon', color: '🟡', label: `Buffet avautuu klo ${openStr}`, fi: `Avautuu klo ${openStr}` };
    if (nowMin >= openMin && nowMin < closeMin) return { kind: 'open', color: '🟢', label: `Buffet käynnissä nyt (${openStr}–${closeStr})`, fi: 'Buffet käynnissä nyt' };
    return { kind: 'closed', color: '⚪', label: 'Buffet päättyi tänään', fi: 'Buffet päättyi tänään' };
  })();
  
  // Function to get date for a specific day of current week
  const getDateForDay = (targetDayIndex) => {
    const diff = targetDayIndex - currentDayOfWeek;
    const targetDate = new Date(now);
    targetDate.setDate(now.getDate() + diff);
    return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Map day keys to day indices
  const dayIndexMap = {
    'monday': 1,
    'tuesday': 2,
    'wednesday': 3,
    'thursday': 4,
    'friday': 5,
    'weekend': currentDayOfWeek === 0 ? 0 : 6
  };

  const MenuItem = ({ item, showImage = false }) => (
    <div className="group bg-white rounded-xl p-5 hover:shadow-md transition-all duration-300 border border-[#2F2F2F]/10">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-medium text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              {item.name}
            </h3>
            {item.vegan && <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">V</span>}
            {item.spicy && <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Spicy</span>}
          </div>
          {item.description && (
            <p className="text-[#6B705C] text-sm">{item.description}</p>
          )}
        </div>
        {item.price && (
          <span className="text-xl font-semibold text-[#C97A2B] ml-4">{item.price}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Hero Section - Clean & Minimal */}
      <section className="relative h-64 md:h-72 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_resto-design-1/artifacts/fulpje03_FB_IMG_1759419329222.jpg)' }}
        />
        <div className="absolute inset-0 bg-[#2F2F2F]/60" />
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="text-center text-white">
            <span className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase">Our Menu</span>
            <h1 className="text-4xl md:text-5xl mt-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              {activeMenu === 'lunch' ? 'Daily Lunch' : 'À la Carte'}
            </h1>
          </div>
        </div>
      </section>

      {/* Navigation - Minimal */}
      <nav className="sticky top-[70px] z-40 bg-white/95 backdrop-blur-lg border-b border-[#2F2F2F]/10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-center gap-2 py-4">
            <button
              onClick={() => setActiveMenu('lunch')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeMenu === 'lunch'
                  ? 'bg-[#2F2F2F] text-white'
                  : 'text-[#2F2F2F] hover:bg-[#2F2F2F]/10'
              }`}
            >
              Lunch Buffet
            </button>
            <button
              onClick={() => {
                setActiveMenu('alacarte');
                setSelectedCategory('appetizers');
              }}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeMenu === 'alacarte'
                  ? 'bg-[#2F2F2F] text-white'
                  : 'text-[#2F2F2F] hover:bg-[#2F2F2F]/10'
              }`}
            >
              À la Carte
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {activeMenu === 'lunch' ? (
          <div>
            {/* Premium Header */}
            <div className="text-center mb-12">
              <p className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase mb-3" data-testid="lunch-week-label">
                {getLunchWeek(new Date(), lang).label}
              </p>
              <h2 className="text-3xl md:text-4xl text-[#2F2F2F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Grilled & Curry Buffet
              </h2>
              <div className="w-16 h-[2px] bg-[#C97A2B] mx-auto mt-4 mb-6" />
              <PrintMenuButton label={ui.print} />
            </div>

            {/* Festival Offer Banner */}
            {lunchMenu.festivalOffer?.active && (
              <div className="max-w-3xl mx-auto mb-6" data-testid="festival-offer-banner">
                <div className="bg-gradient-to-r from-[#C97A2B] via-[#B65A3A] to-[#C97A2B] rounded-full px-5 py-2.5 flex items-center justify-center gap-2 shadow-lg relative overflow-hidden">
                  <Sparkles className="w-4 h-4 text-white flex-shrink-0" />
                  <span className="text-white text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold">
                    {lunchMenu.festivalOffer.label}
                  </span>
                  <span className="text-white/85 text-[11px] md:text-xs hidden sm:inline">
                    · {lunchMenu.festivalOffer.note}
                  </span>
                </div>
                <p className="sm:hidden text-center text-[#6B705C] text-[11px] mt-2">
                  {lunchMenu.festivalOffer.note}
                </p>
              </div>
            )}

            {/* Compact Pricing Row */}
            <div className="grid grid-cols-3 gap-2.5 mb-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-3 text-center border border-[#D9D9D6] shadow-sm">
                <p className="text-[9px] uppercase tracking-widest text-[#6B705C] mb-0.5">Mon – Fri · 11–15</p>
                <div className="flex items-baseline justify-center gap-1">
                  <p className="text-xl font-bold text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>{lunchMenu.priceInfo.regular}</p>
                  {lunchMenu.festivalOffer?.active && lunchMenu.priceInfo.original && (
                    <p className="text-[11px] text-[#6B705C]/60 line-through">{lunchMenu.priceInfo.original}</p>
                  )}
                </div>
                <p className="text-[10px] text-[#6B705C] mt-0.5 leading-tight">
                  {lunchMenu.festivalOffer?.active
                    ? 'All customers'
                    : <>Opisk. {lunchMenu.priceInfo.student} · El. {lunchMenu.priceInfo.pensioner}</>}
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-xl p-3 text-center shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-[#C97A2B] text-white text-[8px] tracking-widest uppercase px-2 py-0.5 rounded-bl">Special</div>
                <p className="text-[9px] uppercase tracking-widest text-[#D9D9D6] mb-0.5">Saturday · 12–16</p>
                <div className="flex items-baseline justify-center gap-1">
                  <p className="text-xl font-bold text-white" style={{ fontFamily: 'DM Serif Display, serif' }}>{lunchMenu.priceInfo.weekend.regular}</p>
                  {lunchMenu.festivalOffer?.active && lunchMenu.priceInfo.weekend.original && (
                    <p className="text-[11px] text-white/40 line-through">{lunchMenu.priceInfo.weekend.original}</p>
                  )}
                </div>
                <p className="text-[10px] text-[#D9D9D6] mt-0.5 leading-tight">
                  {lunchMenu.festivalOffer?.active
                    ? 'All customers'
                    : <>Opisk. {lunchMenu.priceInfo.weekend.student} · El. {lunchMenu.priceInfo.weekend.pensioner}</>}
                </p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center border border-[#D9D9D6] shadow-sm">
                <p className="text-[9px] uppercase tracking-widest text-[#6B705C] mb-0.5">Sunday · 12–20</p>
                <p className="text-sm font-bold text-[#2F2F2F] mt-0.5" style={{ fontFamily: 'DM Serif Display, serif' }}>À la Carte</p>
                <p className="text-[10px] text-[#6B705C] mt-0.5 leading-tight">No lunch buffet</p>
              </div>
            </div>

            {/* Compact Family Pricing + Everyday Includes */}
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <div className="bg-gradient-to-br from-[#C97A2B]/10 to-[#C97A2B]/5 border border-[#C97A2B]/25 rounded-2xl p-4 relative overflow-hidden" data-testid="kids-menu-banner">
                <div className="absolute -top-6 -right-6 w-28 h-28 bg-[#C97A2B]/15 rounded-full blur-2xl pointer-events-none" />
                <div className="relative">
                  <p className="text-[#C97A2B] text-[9px] tracking-[0.25em] uppercase font-bold mb-1">Family Friendly</p>
                  <h3 className="text-base md:text-lg text-[#2F2F2F] leading-tight mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    Kids under 6 eat <span className="italic text-[#C97A2B]">FREE</span>
                  </h3>
                  <div className="flex items-center gap-2 text-[11px] text-[#6B705C] flex-wrap">
                    <span><strong className="text-[#C97A2B]">0–5</strong> Free</span>
                    <span className="opacity-40">·</span>
                    <span><strong className="text-[#2F2F2F]">6–10</strong> Kids price</span>
                    <span className="opacity-40">·</span>
                    <span><strong className="text-[#2F2F2F]">11+</strong> Regular</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2F2F2F] rounded-2xl p-4 relative overflow-hidden" data-testid="everyday-banner">
                <div className="relative">
                  <p className="text-[#C97A2B] text-[9px] tracking-[0.25em] uppercase font-bold mb-1.5">Included Every Day</p>
                  <p className="text-white/90 text-[12px] md:text-[13px] leading-snug">
                    {lunchMenu.everyday.map(i => i.name).join(' · ')}
                  </p>
                </div>
              </div>
            </div>

            {/* 16+ Salads Highlight Banner */}
            {lunchMenu.saladHighlight && (
              <div className="mb-6 bg-gradient-to-br from-[#F7E9A0]/40 via-white to-[#C97A2B]/5 border-2 border-[#C97A2B]/30 rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="salad-highlight-banner">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-[#C97A2B]/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex items-start gap-4">
                  <div className="hidden sm:flex w-14 h-14 rounded-full bg-[#C97A2B]/15 border-2 border-[#C97A2B]/40 items-center justify-center flex-shrink-0 text-2xl">
                    🥗
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-green-700 text-[10px] tracking-[0.3em] uppercase font-bold">{ui.signature}</span>
                      <span className="text-[9px] bg-green-600 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">{ui.fresh}</span>
                    </div>
                    <h3 className="text-xl md:text-2xl text-[#2F2F2F] leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      {locBase(lunchMenu.saladHighlight, 'title', lang)}
                    </h3>
                    <p className="text-[#C97A2B] text-sm font-semibold mt-0.5">
                      {locBase(lunchMenu.saladHighlight, 'subtitle', lang)}
                    </p>
                    <p className="text-[#6B705C] text-[13px] mt-2 leading-relaxed">
                      {locBase(lunchMenu.saladHighlight, 'description', lang)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warm Dishes Intro */}
            {lunchMenu.warmDishesIntro && (
              <div className="mb-6 bg-[#1A1A1A] text-white rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="warm-dishes-intro">
                <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#C97A2B]/15 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex items-start gap-4">
                  <div className="hidden sm:flex w-14 h-14 rounded-full bg-[#C97A2B]/20 border-2 border-[#C97A2B]/40 items-center justify-center flex-shrink-0 text-2xl">
                    🔥
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase font-bold">Lämpimät ruoat · Warm dishes</span>
                    </div>
                    <p className="text-white/90 text-[13px] md:text-[14px] leading-relaxed">
                      {lunchMenu.warmDishesIntro.fi}
                    </p>
                    <p className="text-white/60 text-[12px] italic leading-relaxed mt-2">
                      {lunchMenu.warmDishesIntro.en}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Live Status Badge */}
            <div className="flex justify-center mb-5" data-testid="live-status-badge">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${
                liveStatus.kind === 'open'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : liveStatus.kind === 'soon'
                    ? 'bg-amber-50 border border-amber-200 text-amber-800'
                    : 'bg-gray-50 border border-gray-200 text-gray-600'
              }`}>
                <span>{liveStatus.color}</span>
                <span>{liveStatus.label}</span>
              </div>
            </div>

            {/* Sticky Day + Filter Bar */}
            <div className="sticky top-[70px] z-30 bg-[#F5F3EF]/95 backdrop-blur-md -mx-6 md:mx-0 px-6 md:px-0 py-3 mb-6 md:rounded-xl md:border md:border-[#D9D9D6]/60 md:shadow-sm">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3" data-testid="day-selector">
                {(() => {
                  const dayShortLabels = { monday: 'MA', tuesday: 'TI', wednesday: 'KE', thursday: 'TO', friday: 'PE', weekend: 'LA' };
                  const dayIdxMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, weekend: 6 };
                  return Object.keys(dailyMenus).map((dayKey) => {
                    const isToday = currentDayOfWeek === dayIdxMap[dayKey];
                    const isSelected = selectedDay === dayKey;
                    return (
                      <button
                        key={dayKey}
                        onClick={() => setSelectedDay(dayKey)}
                        className={`relative flex flex-col items-center justify-center min-w-[52px] md:min-w-[64px] px-3 py-2 rounded-xl transition-all duration-200 ${
                          isSelected
                            ? 'bg-[#C97A2B] text-white shadow-md shadow-[#C97A2B]/30'
                            : isToday
                              ? 'bg-white border-2 border-[#C97A2B] text-[#C97A2B]'
                              : 'bg-white border border-[#D9D9D6] text-[#6B705C] hover:border-[#C97A2B]/50'
                        }`}
                        data-testid={`day-btn-${dayKey}`}
                      >
                        <span className="text-sm md:text-base font-bold tracking-wider">{dayShortLabels[dayKey]}</span>
                        {isToday && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider mt-0.5 flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#C97A2B]'} animate-pulse`} />
                            Tänään
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
                {/* Sunday placeholder (closed) */}
                <div className="hidden md:flex flex-col items-center justify-center min-w-[64px] px-3 py-2 rounded-xl bg-gray-100 border border-gray-200 text-gray-400">
                  <span className="text-base font-bold tracking-wider">SU</span>
                  <span className="text-[9px] uppercase tracking-wider mt-0.5">À la carte</span>
                </div>

                {/* Filter buttons */}
                <div className="w-full md:w-auto flex justify-center gap-2 md:ml-2 md:border-l md:border-[#D9D9D6] md:pl-3 mt-2 md:mt-0">
                  <button
                    onClick={() => setFilterVegan(v => !v)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
                      filterVegan ? 'bg-green-600 text-white' : 'bg-white border border-[#D9D9D6] text-[#6B705C] hover:border-green-500'
                    }`}
                    data-testid="filter-vegan"
                  >
                    🌱 {ui.vegan}
                  </button>
                  <button
                    onClick={() => setFilterGF(v => !v)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
                      filterGF ? 'bg-amber-600 text-white' : 'bg-white border border-[#D9D9D6] text-[#6B705C] hover:border-amber-500'
                    }`}
                    data-testid="filter-gf"
                  >
                    🌾 {ui.gf}
                  </button>
                </div>
              </div>

              {/* Palaa tähän päivään — shown when a non-today day is selected */}
              {(() => {
                const dayIdxMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, weekend: 6 };
                const todayKey = Object.keys(dayIdxMap).find(k => dayIdxMap[k] === currentDayOfWeek);
                if (!todayKey || selectedDay === todayKey) return null;
                return (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setSelectedDay(todayKey)}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#C97A2B]/10 border border-[#C97A2B]/30 text-[#C97A2B] text-xs font-semibold hover:bg-[#C97A2B]/20 transition-colors"
                      data-testid="back-to-today-btn"
                    >
                      ← Palaa tähän päivään
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Selected Day Card */}
            {(() => {
              const menu = dailyMenus[selectedDay];
              const dayIndexMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, weekend: 6 };
              const isToday = currentDayOfWeek === dayIndexMap[selectedDay];

              return (
                <div
                  className={`bg-white rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-500 ${
                    isToday ? 'border-[#C97A2B]' : 'border-[#D9D9D6]'
                  }`}
                  data-testid="selected-day-menu"
                >
                  {/* Day Header */}
                  <div className={`px-8 py-6 flex items-center justify-between ${
                    isToday 
                      ? 'bg-gradient-to-r from-[#C97A2B]/15 to-[#C97A2B]/5' 
                      : 'bg-gradient-to-r from-[#F5F3EF] to-white'
                  }`}>
                    <div>
                      <h3 className="text-2xl text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                        {locText(menu, 'day', lang)}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3.5 h-3.5 text-[#C97A2B]" />
                        <span className="text-sm text-[#6B705C]">{menu.hours.replace('-', '–')}</span>
                      </div>
                    </div>
                    {isToday && (
                      <span className="px-4 py-1.5 bg-[#C97A2B] text-white text-xs font-semibold rounded-full tracking-wider uppercase shadow-md">
                        {ui.today}
                      </span>
                    )}
                  </div>

                  {/* Menu Content */}
                  <div className="p-6 md:p-8">
                    {menu.promo?.active && (
                      <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#C97A2B] via-[#B65A3A] to-[#C97A2B] p-[2px] shadow-xl" data-testid="weekend-promo-banner">
                        <div className="bg-[#1A1A1A] rounded-[calc(1rem-2px)] px-5 py-4 md:px-6 md:py-5 relative overflow-hidden">
                          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#C97A2B]/25 rounded-full blur-3xl pointer-events-none" />
                          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#B65A3A]/20 rounded-full blur-3xl pointer-events-none" />
                          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="w-4 h-4 text-[#C97A2B]" />
                                <span className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase font-bold">
                                  Festive Special
                                </span>
                              </div>
                              <p className="text-white text-[15px] md:text-base leading-snug font-medium">
                                {menu.promo.headline}
                              </p>
                            </div>
                            {menu.promo.price && (
                              <div className="flex-shrink-0 bg-white/10 border border-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
                                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Only</p>
                                <p className="text-white text-xl md:text-2xl font-bold leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
                                  {menu.promo.price}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {menu.tagline && (
                      <div className="mb-6 -mt-2 flex items-start gap-3 rounded-2xl bg-gradient-to-r from-[#1A1A1A] to-[#2F2F2F] px-5 py-4 relative overflow-hidden">
                        <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#C97A2B]/15 rounded-full blur-3xl pointer-events-none" />
                        <Sparkles className="w-4 h-4 text-[#C97A2B] flex-shrink-0 mt-0.5 relative" />
                        <div className="relative">
                          <p className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase font-semibold mb-0.5">{ui.weekend}</p>
                          <p className="text-white text-[13px] md:text-[14px] italic leading-snug">{locText(menu, 'tagline', lang)}</p>
                        </div>
                      </div>
                    )}
                    {menu.comingSoon ? (
                      <div className="text-center py-10">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C97A2B]/15 border border-[#C97A2B]/30 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-[#C97A2B]" />
                        </div>
                        <p className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase font-semibold mb-3">Coming Soon</p>
                        <h4 className="text-xl md:text-2xl text-[#2F2F2F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                          Special Saturday Menu
                        </h4>
                        <p className="text-[#6B705C] text-sm max-w-md mx-auto">
                          {menu.comingSoonMessage || 'Our special Saturday menu is coming soon — stay tuned!'}
                        </p>
                      </div>
                    ) : (
                    <div className="grid md:grid-cols-3 gap-8">
                      {/* Column 1: Starters & Salads (optional) */}
                      {menu.starters && menu.starters.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Leaf className="w-4 h-4 text-green-600" />
                            <h4 className="text-xs text-[#6B705C] uppercase tracking-[0.15em] font-semibold">Starters & Salads</h4>
                          </div>
                          <ul className="space-y-3">
                            {menu.starters.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 group">
                                <span className="text-[#C97A2B] mt-0.5 group-hover:scale-125 transition-transform">•</span>
                                <span className="text-sm text-[#2F2F2F] leading-relaxed">{item.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Column 2: Main Course – spans wider when starters are absent */}
                      <div className={menu.starters && menu.starters.length > 0 ? '' : 'md:col-span-2'}>
                        <div className="flex items-center gap-2 mb-5">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <h4 className="text-xs text-[#6B705C] uppercase tracking-[0.15em] font-semibold">{ui.mains}</h4>
                        </div>
                        <ul className="space-y-4">
                          {(menu.mains || []).filter((item) => {
                            if (filterVegan && !(item.vegan || (item.dietary || []).includes('V'))) return false;
                            if (filterGF && !(item.dietary || []).includes('G')) return false;
                            return true;
                          }).map((item, idx) => (
                            <li key={idx} className="group">
                              <div className="flex items-baseline gap-3">
                                <span className="text-[#C97A2B] group-hover:scale-125 transition-transform">•</span>
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                    <span className="text-[15px] text-[#2F2F2F] font-medium leading-tight" data-testid={`main-name-${idx}`}>{locText(item, 'name', lang)}</span>
                                    {item.chefPick && <ChefPickBadge testId={`chef-pick-badge-${idx}`} label={ui.chefPick} />}
                                    {item.spicy && <span className="text-red-500 text-xs">🌶️</span>}
                                    {item.dietary && item.dietary.length > 0 && (
                                      <span className="flex gap-1">
                                        {item.dietary.map((d) => (
                                          <span key={d} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${d === 'V' || d === 'VE' ? 'text-green-700 bg-green-50' : 'text-[#6B705C] bg-[#F5F3EF]'}`}>
                                            {d}
                                          </span>
                                        ))}
                                      </span>
                                    )}
                                  </div>
                                  {origName(item, lang) && <p className="text-[11px] text-[#9A9A94] italic leading-tight mt-0.5">{origName(item, lang)}</p>}
                                  {locText(item, 'description', lang) && (
                                    <p className="text-[13px] text-[#6B705C] leading-relaxed mt-1">{locText(item, 'description', lang)}</p>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Column 3: Dessert (only when we have desserts this week) */}
                      {menu.desserts && menu.desserts.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Utensils className="w-4 h-4 text-[#C97A2B]" />
                            <h4 className="text-xs text-[#6B705C] uppercase tracking-[0.15em] font-semibold">{ui.dessert}</h4>
                          </div>
                          <div className="space-y-3">
                            {menu.desserts.map((item, idx) => (
                              <div key={idx} className="bg-[#C97A2B]/8 rounded-xl px-4 py-3 border border-[#C97A2B]/15">
                                <span className="text-sm text-[#2F2F2F] font-medium">{locText(item, 'name', lang)}</span>
                                {locText(item, 'description', lang) && <p className="text-[12px] text-[#6B705C] mt-1 leading-snug" data-testid={`dessert-desc-${idx}`}>{locText(item, 'description', lang)}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Served With — per-day sides if present, else the daily baseline (lunchMenu.everyday) */}
                    {!menu.comingSoon && ((menu.sides && menu.sides.length > 0) || (lunchMenu.everyday && lunchMenu.everyday.length > 0)) && (
                      <div className="mt-8 pt-6 border-t border-[#D9D9D6]">
                        <div className="flex items-center gap-2 mb-3">
                          <Utensils className="w-4 h-4 text-[#C97A2B]" />
                          <h4 className="text-xs text-[#6B705C] uppercase tracking-[0.15em] font-semibold">{ui.sides}</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(menu.sides && menu.sides.length > 0 ? menu.sides : lunchMenu.everyday).map((item, i) => (
                            <span key={i} className="text-[13px] text-[#2F2F2F] bg-[#F5F3EF] border border-[#D9D9D6] px-3 py-1.5 rounded-full">
                              {locText(item, 'name', lang)}
                              {item.dietary && item.dietary.length > 0 && (
                                <span className="ml-1 text-[10px] text-[#6B705C]">({item.dietary.join(', ')})</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quality Tags Footer */}
                  <div className="px-8 pb-6">
                    <div className="flex flex-wrap justify-center gap-2 md:gap-3 pt-5 border-t border-[#D9D9D6]">
                      <span className="flex items-center gap-1.5 text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        <Leaf className="w-3 h-3 text-green-600" /> {ui.fresh}
                      </span>
                      <span className="flex items-center gap-1.5 text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        <Flame className="w-3 h-3 text-orange-500" /> {ui.fire}
                      </span>
                      <span className="flex items-center gap-1.5 text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        {ui.handmade}
                      </span>
                      <span className="text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        {ui.legendL}
                      </span>
                      <span className="text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        {ui.legendG}
                      </span>
                      <span className="text-[11px] text-[#6B705C] bg-[#F5F3EF] px-3 py-1.5 rounded-full">
                        {ui.legendM}
                      </span>
                      <span className="text-[11px] text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
                        {ui.legendV}
                      </span>
                      <span className="text-[11px] text-red-600 bg-red-50 px-3 py-1.5 rounded-full">
                        {ui.spicy}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Share + Allergens + Catering strip */}
            {(() => {
              const menu = dailyMenus[selectedDay];
              if (!menu || menu.comingSoon) return null;
              return (
                <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto] items-stretch">
                  <ShareLunch menu={menu} dayKey={selectedDay} lang={lang} />
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
                  <button
                    onClick={() => setShowAllergenPopup(true)}
                    className="flex items-center justify-center gap-2 py-3.5 px-5 bg-white border border-[#D9D9D6] text-[#6B705C] rounded-full text-xs font-semibold hover:border-[#C97A2B] hover:text-[#C97A2B] transition-colors"
                    data-testid="allergens-btn"
                  >
                    🌱 {ui.allergens}
                  </button>
                  <button
                    onClick={() => navigate('/ehdota')}
                    className="flex items-center justify-center gap-2 py-3.5 px-5 bg-white border border-[#D9D9D6] text-[#6B705C] rounded-full text-xs font-semibold hover:border-[#C97A2B] hover:text-[#C97A2B] transition-colors"
                    data-testid="suggest-dish-link"
                  >
                    🍲 {ui.suggest}
                  </button>
                  </div>
                </div>
              );
            })()}

            {/* Catering mini-banner */}
            <div className="mt-6 bg-gradient-to-r from-[#2F2F2F] to-[#1A1A1A] rounded-2xl px-5 py-4 md:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 relative overflow-hidden" data-testid="catering-mini-banner">
              <div className="absolute -top-8 -right-10 w-40 h-40 bg-[#C97A2B]/15 rounded-full blur-3xl pointer-events-none" />
              <div className="relative flex-1">
                <p className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase font-bold mb-0.5">{ui.cateringKicker}</p>
                <p className="text-white text-sm md:text-[15px]">{ui.cateringText}</p>
              </div>
              <button
                onClick={() => navigate('/catering')}
                className="relative flex-shrink-0 px-5 py-2.5 bg-[#C97A2B] text-white rounded-full text-xs font-bold tracking-wider uppercase hover:bg-[#B65A3A] transition-colors"
                data-testid="catering-cta-btn"
              >
                {ui.cateringCta}
              </button>
            </div>

            {/* Allergen popup */}
            {showAllergenPopup && (
              <div className="fixed inset-0 z-[9000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAllergenPopup(false)}>
                <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="allergen-popup">
                  <h3 className="text-xl text-[#2F2F2F] mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>Allergeenit & ruokavalio</h3>
                  <ul className="space-y-2 text-sm text-[#6B705C]">
                    <li><strong className="text-[#2F2F2F]">L</strong> — Laktoositon</li>
                    <li><strong className="text-[#2F2F2F]">G</strong> — Gluteeniton</li>
                    <li><strong className="text-[#2F2F2F]">V</strong> — Vegaaninen</li>
                    <li><strong className="text-[#2F2F2F]">VE</strong> — Vegaanivaihtoehto saatavilla</li>
                    <li><strong className="text-[#2F2F2F]">M</strong> — Maidoton</li>
                    <li><strong className="text-[#2F2F2F]">🌶</strong> — Tulinen</li>
                  </ul>
                  <p className="text-xs text-[#6B705C] mt-4 pt-4 border-t border-[#D9D9D6]">
                    Jos sinulla on erityinen ruoka-aine-allergia, kysy aina henkilökunnalta ennen tilauksen tekemistä. Soita <a href="tel:0403779200" className="text-[#C97A2B] font-semibold">040 377 9200</a>.
                  </p>
                  <button onClick={() => setShowAllergenPopup(false)} className="mt-5 w-full py-2.5 bg-[#2F2F2F] text-white rounded-full text-xs font-semibold uppercase tracking-wider hover:bg-[#C97A2B] transition-colors">
                    Sulje
                  </button>
                </div>
              </div>
            )}

            {/* CTA to À la Carte */}
            <div className="mt-12 text-center">
              <p className="text-[#6B705C] mb-4">{ui.more}</p>
              <button
                onClick={() => {
                  setActiveMenu('alacarte');
                  setSelectedCategory('appetizers');
                }}
                className="px-8 py-3 bg-[#2F2F2F] text-white rounded-full font-medium hover:bg-[#C97A2B] transition-colors"
                data-testid="view-alacarte-btn"
              >
                {ui.viewAlacarte}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* À la Carte Menu */}
            <div className="text-center mb-8">
              <h2 className="text-5xl text-[#1A1A1A] mb-4 tracking-wide" style={{ fontFamily: 'DM Serif Display, serif' }}>{alaCarteMenu.title}</h2>
              <p className="text-[#1A1A1A]/70 text-lg max-w-3xl mx-auto">{alaCarteMenu.description}</p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-[#2F2F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search dishes by name or ingredient..."
                  className="w-full pl-12 pr-4 py-4 bg-white/50 backdrop-blur-sm border-2 border-[#2F2F2F]/30 rounded-2xl text-[#1A1A1A] placeholder-[#1A1A1A]/50 focus:outline-none focus:border-[#2F2F2F] transition-all duration-300 text-lg"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#2F2F2F] hover:text-[#2F2F2F]/70"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-[#1A1A1A]/60 text-sm mt-2 text-center">
                  Searching for "{searchQuery}"...
                </p>
              )}
            </div>

            {/* Made From Scratch — Premium Callout */}
            <div className="max-w-6xl mx-auto mb-8" data-testid="from-scratch-banner">
              <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2F2F2F] rounded-3xl px-8 py-7 relative overflow-hidden border border-[#C97A2B]/20">
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#C97A2B]/15 rounded-full blur-3xl" />
                <div className="absolute -bottom-16 -left-12 w-56 h-56 bg-[#B65A3A]/10 rounded-full blur-3xl" />
                <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
                  <div className="flex-shrink-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-[1px] bg-[#C97A2B]" />
                      <span className="text-[#C97A2B] text-[10px] tracking-[0.3em] uppercase font-semibold">Sunsari Promise</span>
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      Everything is <span className="italic text-[#C97A2B]">made from scratch</span>
                    </h3>
                  </div>
                  <p className="text-white/75 text-sm md:text-[15px] leading-relaxed flex-1">
                    No ready-made sauces. No pre-cooked meats. Every curry base, every marinade, every spice blend is hand-prepared in our kitchen — meats are fresh, vegetables are seasonal, and our sauces simmer slowly until they reach the depth of flavour we serve. The way it has been done in our family kitchens for generations.
                  </p>
                </div>
              </div>
            </div>

            {/* Sauce Guide Section - Collapsible */}
            <div className="max-w-6xl mx-auto mb-12">
              <div className="bg-[#2F2F2F]/10 backdrop-blur-sm rounded-3xl p-6 border-2 border-[#2F2F2F]/30 shadow-xl">
                {/* Collapsed View - Button */}
                <button
                  onClick={() => setSaucesExpanded(!saucesExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#2F2F2F]/10 rounded-xl transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-left">
                      <h3 className="text-2xl text-[#2F2F2F] group-hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: 'DM Serif Display, serif' }}>
                        Our Handmade <span className="italic text-[#C97A2B]">Curry Sauces</span>
                      </h3>
                      <p className="text-[#1A1A1A]/60 text-sm mt-1">
                        Slow-simmered, freshly ground, and crafted in-house — never from a jar
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[#2F2F2F] text-sm font-semibold">
                      {saucesExpanded ? 'Hide Details' : 'Discover Our Sauces'}
                    </span>
                    <svg 
                      className={`w-6 h-6 text-[#2F2F2F] transition-transform duration-300 ${saucesExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Expanded View - Sauce Details */}
                {saucesExpanded && (
                  <div className="mt-6 pt-6 border-t border-[#2F2F2F]/20 animate-fade-in">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Butter Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Butter Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Slow-simmered tomatoes and hand-blended cashews, finished with farm butter and our home-roasted spice blend. Velvety, deep, never sweetened from a tin.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Butter Chicken, Paneer Butter Masala</p>
                        </div>
                      </div>

                      {/* Korma Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Korma Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Cashews and almonds soaked, ground fresh, and gently cooked with cardamom, cinnamon and saffron. Mild, silky and richly aromatic — exactly the way it was passed down to us.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Chicken Korma, Vegetable Korma</p>
                        </div>
                      </div>

                      {/* Masala Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Masala Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Caramelised onions and ripe tomatoes folded into our own garam masala — cumin, coriander, and black cardamom toasted and ground in-house. Bold, layered and unmistakably ours.
                        </p>
                        <div className="mt-3 pt-3 border-t border-amber-500/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Tikka Masala, Paneer Masala</p>
                        </div>
                      </div>

                      {/* Curry Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Classic Curry Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Our family curry base — onions, ginger and garlic browned slowly, then enriched with whole spices tempered in mustard oil. Earthy, warm, and built layer by layer in the pan.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Lamb Curry, Chicken Curry</p>
                        </div>
                      </div>

                      {/* Tomato Chilli Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Tomato Chilli</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Roasted tomatoes blistered with green and Kashmiri chillies, finished with a splash of soy and our house-made chilli paste. Honest heat — for those who like it bold.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Chilli Chicken, Spicy Specialties</p>
                        </div>
                      </div>

                      {/* Spinach Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Saag (Spinach) Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Fresh spinach blanched and stone-ground the same day, tempered with garlic, ginger and roasted cumin. Bright, leafy, and made fresh every morning — never from frozen purée.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Palak Paneer, Saag Chicken</p>
                        </div>
                      </div>

                      {/* Tikka Sauce */}
                      <div className="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-[#2F2F2F]/20 hover:border-[#C97A2B]/50 transition-all duration-300 hover:transform hover:scale-105">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-xl font-bold text-[#2F2F2F]">Tikka Sauce</h4>
                          <span className="text-[9px] tracking-widest uppercase text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full font-bold">Handmade</span>
                        </div>
                        <p className="text-[#1A1A1A]/80 text-sm leading-relaxed">
                          Yogurt-marinated meats kissed by the tandoor, finished in a sauce of charred tomatoes, ginger-garlic paste and our own tandoori spice mix. Smoky, tangy, and full of fire-oven character.
                        </p>
                        <div className="mt-3 pt-3 border-t border-[#2F2F2F]/10">
                          <p className="text-[#2F2F2F]/60 text-xs">Popular in: Chicken Tikka, Tandoori Specials</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center mt-6 pt-6 border-t border-[#2F2F2F]/20">
                      <p className="text-[#1A1A1A]/70 text-sm italic">
                        Every sauce is freshly prepared each day from whole spices and fresh produce — never reheated from yesterday, never poured from a jar. The recipes have travelled with our family from the foothills of Nepal to your table.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Category Navigation */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {Object.keys(alaCarteMenu).map((category) => {
                if (!alaCarteMenu[category]?.items) return null; // Skip non-category keys
                const filteredItems = searchQuery 
                  ? alaCarteMenu[category].items.filter(item =>
                      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      item.description.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : alaCarteMenu[category].items;
                
                return (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setSearchQuery('');
                    }}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      selectedCategory === category
                        ? 'bg-[#2F2F2F] text-white shadow-xl'
                        : 'bg-white/50 text-[#2F2F2F] hover:bg-[#2F2F2F]/10 border border-[#2F2F2F]/30'
                    }`}
                  >
                    {alaCarteMenu[category].title}
                    {searchQuery && filteredItems.length > 0 && (
                      <span className="ml-2 text-xs bg-[#1A1A1A]/20 px-2 py-0.5 rounded-full">
                        {filteredItems.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Category Display */}
            {searchQuery ? (
              // Search Results View
              <div>
                <h3 className="text-3xl text-[#1A1A1A] mb-6 text-center" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  Search Results
                </h3>
                {Object.keys(alaCarteMenu).map((category) => {
                  if (!alaCarteMenu[category]?.items) return null;
                  const filteredItems = alaCarteMenu[category].items.filter(item =>
                    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.description.toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  
                  if (filteredItems.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-12">
                      <div className="flex items-center gap-3 mb-6">
                        <h4 className="text-2xl text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                          {alaCarteMenu[category].title}
                        </h4>
                        <span className="text-sm text-[#1A1A1A]/60">({filteredItems.length} {filteredItems.length === 1 ? 'dish' : 'dishes'})</span>
                      </div>
                      <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-1">
                        {filteredItems.map((item, idx) => (
                          <MenuItem key={idx} item={item} showImage={true} />
                        ))}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(alaCarteMenu).filter(cat => alaCarteMenu[cat]?.items).every(category => 
                  alaCarteMenu[category].items.filter(item =>
                    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.description.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0
                ) && (
                  <div className="text-center py-12">
                    <p className="text-[#1A1A1A]/60 text-lg">No dishes found matching "{searchQuery}"</p>
                    <p className="text-[#1A1A1A]/40 text-sm mt-2">Try a different search term</p>
                  </div>
                )}
              </div>
            ) : selectedCategory && alaCarteMenu[selectedCategory] ? (
              // Category View
              <div>
                <h3 className="text-4xl text-[#1A1A1A] mb-3 text-center tracking-wide" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {alaCarteMenu[selectedCategory].title}
                </h3>
                {alaCarteMenu[selectedCategory].subtitle && (
                  <p className="text-[#2F2F2F]/70 text-center mb-10 italic text-lg">
                    {alaCarteMenu[selectedCategory].subtitle}
                  </p>
                )}
                
                <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-1">
                  {alaCarteMenu[selectedCategory].items.map((item, idx) => (
                    <MenuItem key={idx} item={item} showImage={true} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/combine-tables')}
            className="bg-[#2F2F2F] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#1A1A1A] transform hover:scale-105 transition-all duration-300 shadow-xl"
            data-testid="menu-reserve-btn"
          >
            Reserve Your Table
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedMenuPage;